<template>
  <div class="material-check-page frame-page inventory-import-page" :class="{ 'is-loading': loading }">
    <div class="page-intro">
      <p>
        将 SAP ME2L 采购清单或 MB51 导出数据转换为物资库导入 Excel。支持 .mhtml / .mht / .xls / .xlsx，
        两个功能相互独立，分别上传对应文件后点击生成。
      </p>
    </div>

    <input ref="me2lInput" type="file" hidden accept=".mht,.mhtml,.xls,.xlsx" @change="onMe2lSelected" />
    <input ref="mb51Input" type="file" hidden accept=".mht,.mhtml,.xls,.xlsx" @change="onMb51Selected" />

    <section class="panel">
      <div class="panel-title">
        <span class="step">1</span>
        <span>ME2L 采购清单</span>
        <span v-if="me2lPath" class="tag tag-done">已上传</span>
        <span v-else class="tag tag-muted">待选择</span>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !me2lPath,
          'is-dragover': me2lDragOver,
          'is-filled': me2lPath,
        }"
        @click="pickMe2l"
        @dragenter.prevent="setDragOver('me2l', true)"
        @dragover.prevent="setDragOver('me2l', true)"
        @dragleave.prevent="setDragOver('me2l', false)"
        @drop.prevent="onDropMe2l"
      >
        <div class="drop-zone-body">
          <div class="drop-icon" :class="{ 'is-uploaded': me2lPath }">
            <span v-if="me2lPath" class="drop-check">✓</span>
            <span v-else>ME2L</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !me2lPath }">
              {{ me2lPath || '选择或拖拽 ME2L 采购清单' }}
            </div>
            <div class="drop-hint">
              <template v-if="me2lPath">已读取 {{ me2lRowCount }} 行</template>
              <template v-else>需含删除标识、物料、采购凭证等列</template>
            </div>
          </div>
          <div class="drop-actions" @click.stop>
            <Button size="s" class="drop-btn-clear" @click="clearMe2l">清除</Button>
            <Button size="s" color="primary" @click="pickMe2l">浏览</Button>
          </div>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span class="step">2</span>
        <span>MB51 导出数据</span>
        <span v-if="mb51Path" class="tag tag-done">已上传</span>
        <span v-else class="tag tag-muted">待选择</span>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !mb51Path,
          'is-dragover': mb51DragOver,
          'is-filled': mb51Path,
        }"
        @click="pickMb51"
        @dragenter.prevent="setDragOver('mb51', true)"
        @dragover.prevent="setDragOver('mb51', true)"
        @dragleave.prevent="setDragOver('mb51', false)"
        @drop.prevent="onDropMb51"
      >
        <div class="drop-zone-body">
          <div class="drop-icon" :class="{ 'is-uploaded': mb51Path }">
            <span v-if="mb51Path" class="drop-check">✓</span>
            <span v-else>MB51</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !mb51Path }">
              {{ mb51Path || '选择或拖拽 MB51 导出数据' }}
            </div>
            <div class="drop-hint">
              <template v-if="mb51Path">已读取 {{ mb51RowCount }} 行</template>
              <template v-else>需含移动类型、批次、物料、数量等列</template>
            </div>
          </div>
          <div class="drop-actions" @click.stop>
            <Button size="s" class="drop-btn-clear" @click="clearMb51">清除</Button>
            <Button size="s" color="primary" @click="pickMb51">浏览</Button>
          </div>
        </div>
      </div>
    </section>

    <section class="panel actions-panel">
      <div class="panel-title">
        <span class="step">3</span>
        <span>生成导入清单</span>
      </div>
      <div class="action-row">
        <Button color="primary" :disabled="!me2lPath" @click="doMe2lGenerate">项目定制导入清单</Button>
        <Button color="primary" :disabled="!mb51Path" @click="doMb51Generate">集中储备调拨导入清单</Button>
        <Button @click="doReset">清空选择</Button>
        <Button text @click="helpOpen = true">功能说明</Button>
      </div>
      <p v-if="lastResult" class="result-hint">
        最近生成：{{ lastResult.filename }}（{{ lastResult.rowCount }} 行）
        <template v-if="lastResult.path"> · 已用 Excel 打开</template>
      </p>
    </section>

    <Modal
      v-model="helpOpen"
      title="功能详细说明"
      :middle="true"
      hasDivider
      :hasCloseIcon="true"
      :closeOnMask="true"
    >
      <pre class="help-text">{{ helpText }}</pre>
      <template #footer>
        <Button color="primary" @click="helpOpen = false">关闭</Button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { confirm, message } from 'heyui';
import { inventoryImportApi as api } from '@/api/inventoryImport';

const FILE_EXTS = ['.mht', '.mhtml', '.xls', '.xlsx'];

const helpText = `项目定制导入清单逻辑：

1. 读取 ME2L 采购清单，支持 mhtml、mht、xls、xlsx。
2. 检查是否包含：删除标识、物料、短文本、采购订单数量、订单净值、采购凭证、订单单位、凭证日期、供应商/供应工厂。
3. 只保留 删除标识 为空 且 物料 不为空的记录。
4. 物料编号去掉小数后缀；同一 物料+采购凭证 重复出现时，依次追加 A、B、C… 后缀区分。
5. 生成物资库导入字段并导出为「申请导入_起始日期_至_结束日期_随机数.xlsx」。

集中储备调拨导入清单逻辑：

1. 读取 MB51 导出数据，支持 mhtml、mht、xls、xlsx。
2. 检查是否包含：输入日期、移动类型、批次、物料、物料描述、基本计量单位、数量、本位币金额。
3. 只保留移动类型为 Z71（集中储备）或 Z7P（物资调拨）的记录。
4. 按 物料编号 + 采购订单号 汇总数量后导出为「调拨集中储备_起始日期_至_结束日期_随机数.xlsx」。`;

const loading = ref(false);
const me2lPath = ref('');
const mb51Path = ref('');
const me2lRowCount = ref(0);
const mb51RowCount = ref(0);
const me2lInput = ref(null);
const mb51Input = ref(null);
const me2lDragOver = ref(false);
const mb51DragOver = ref(false);
const helpOpen = ref(false);
const lastResult = ref(null);

function matchExtension(name, exts) {
  const lower = (name || '').toLowerCase();
  return exts.some((ext) => lower.endsWith(ext));
}

async function withLoading(fn) {
  loading.value = true;
  try {
    return await fn();
  } finally {
    loading.value = false;
  }
}

function showError(msg) {
  confirm({
    type: 'error',
    title: '错误',
    content: msg || '操作失败',
  });
}

function showSuccess(msg) {
  message.success(msg);
}

function setDragOver(type, active) {
  if (type === 'me2l') me2lDragOver.value = active;
  else mb51DragOver.value = active;
}

function pickMe2l() {
  me2lInput.value?.click();
}

function pickMb51() {
  mb51Input.value?.click();
}

async function uploadMe2lFile(file) {
  if (!file) return;
  if (!matchExtension(file.name, FILE_EXTS)) {
    showError('ME2L 采购清单仅支持 .mht .mhtml .xls .xlsx');
    return;
  }
  const res = await withLoading(() => api.uploadMe2l(file));
  if (!res.ok) {
    showError(res.error);
    return;
  }
  me2lPath.value = res.displayName || file.name;
  me2lRowCount.value = res.rowCount || 0;
  showSuccess(`已读取 ME2L 采购清单，共 ${res.rowCount} 行`);
}

async function uploadMb51File(file) {
  if (!file) return;
  if (!matchExtension(file.name, FILE_EXTS)) {
    showError('MB51 导出数据仅支持 .mht .mhtml .xls .xlsx');
    return;
  }
  const res = await withLoading(() => api.uploadMb51(file));
  if (!res.ok) {
    showError(res.error);
    return;
  }
  mb51Path.value = res.displayName || file.name;
  mb51RowCount.value = res.rowCount || 0;
  showSuccess(`已读取 MB51 导出数据，共 ${res.rowCount} 行`);
}

async function onMe2lSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  await uploadMe2lFile(file);
}

async function onMb51Selected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  await uploadMb51File(file);
}

async function onDropMe2l(event) {
  setDragOver('me2l', false);
  await uploadMe2lFile(event.dataTransfer?.files?.[0]);
}

async function onDropMb51(event) {
  setDragOver('mb51', false);
  await uploadMb51File(event.dataTransfer?.files?.[0]);
}

function applyDownloadResult(res) {
  lastResult.value = {
    filename: res.filename,
    path: res.path || '',
    rowCount: res.rowCount,
    downloadUrl: api.downloadUrl(res.filename),
  };
  // 后端已 os.startfile 打开 Excel，不再弹出浏览器另存为
}

async function doMe2lGenerate() {
  const res = await withLoading(() => api.generateMe2l());
  if (!res.ok) return showError(res.error);
  applyDownloadResult(res);
  showSuccess(res.path ? `项目定制导入清单已生成：${res.path}` : `项目定制导入清单已生成：${res.filename}（${res.rowCount} 行）`);
}

async function doMb51Generate() {
  const res = await withLoading(() => api.generateMb51());
  if (!res.ok) return showError(res.error);
  applyDownloadResult(res);
  showSuccess(res.path ? `集中储备调拨导入清单已生成：${res.path}` : `集中储备调拨导入清单已生成：${res.filename}（${res.rowCount} 行）`);
}

async function clearMe2l() {
  const res = await api.clearMe2l();
  if (!res.ok) return showError(res.error);
  me2lPath.value = '';
  me2lRowCount.value = 0;
  showSuccess('已清除 ME2L 采购清单');
}

async function clearMb51() {
  const res = await api.clearMb51();
  if (!res.ok) return showError(res.error);
  mb51Path.value = '';
  mb51RowCount.value = 0;
  showSuccess('已清除 MB51 导出数据');
}

async function doReset() {
  const res = await api.resetAll();
  if (!res.ok) return showError(res.error);
  me2lPath.value = '';
  mb51Path.value = '';
  me2lRowCount.value = 0;
  mb51RowCount.value = 0;
  lastResult.value = null;
}

onMounted(async () => {
  const res = await api.getState();
  if (res.ok) {
    me2lPath.value = res.me2lDisplayName || '';
    me2lRowCount.value = res.me2lRowCount || 0;
    mb51Path.value = res.mb51DisplayName || '';
    mb51RowCount.value = res.mb51RowCount || 0;
  }
});
</script>

<style lang="less" scoped>
@import '../material-check/material-check.less';

.inventory-import-page {
  .action-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }

  .result-hint {
    margin: 16px 0 0;
    font-size: 13px;
    color: #8b95a5;
    line-height: 1.6;
  }

  .download-link {
    margin-left: 8px;
    color: #3788ee;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .help-text {
    margin: 0;
    white-space: pre-wrap;
    font-size: 13px;
    line-height: 1.7;
    color: #4a5568;
    max-height: 60vh;
    overflow: auto;
  }
}
</style>
